import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { post } from 'api'
import { useSurveys } from 'contexts/surveys'
import { cls } from 'utilities/dom'
import { size } from 'utilities/object'
import { getSurveyUrl } from 'utilities/url'
import Modal, { useModal } from 'modals/dialog'
import Form from 'components/form/controller'
import SymbolField from 'components/form/field/symbol'
import StringField from 'components/form/field/string'
import OneOfField from 'components/form/field/one-of'
import { LabelWithIcon, AiIcon } from './s'
import TextField from 'components/form/field/text'
import CheckboxField from 'components/form/field/checkbox'
import { Helper } from 'components/form/field/s'
import Beta from 'components/beta'
import Message from 'components/message'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'
import { getFallbackSymbol } from 'pages/surveys/utilities'

const AddSurvey = ({ type, salt }) => {
    const { formatMessage } = useIntl()
    const { dismiss } = useModal()

    const navigate = useNavigate()

    const { addSurvey } = useSurveys()

    const [symbol, setSymbol] = useState(null)
    const [strategy, setStrategy] = useState('ai')
    const [aiPrompt, setAiPrompt] = useState('')
    const [questions, setQuestions] = useState('')
    const [adding, setAdding] = useState(false)
    const [error, setError] = useState(false)

    const getAiSuggestion = async userPrompt => {
        const result = await post({
            path: '/content-assistance',
            body: {
                model: 'gpt-4o-mini',
                messages: [
                    {
                        role: 'system',
                        content: systemPrompt
                    },
                    {
                        role: 'user',
                        content: userPrompt
                    }
                ],
                response_format: { type: 'json_object' }
            }
        })

        if(!result.ok) {
            console.error("Error with content assistant:", result.response)
            return result
        }

        const cleanedText = result.response.text.replace(/```json\n|```/g, '').trim()

        let ok = false
        let response = null

        try {
            response = { questions: JSON.parse(cleanedText) }
            ok = true
        } catch (error) {
            ok = false
            console.error("Error parsing JSON:", error)
        }

        const valid = ok && Array.isArray(response.questions) && !!response.questions.length && response.questions.every(question =>
            !!question?.type &&
            !!question?.title &&
            !!question?.meta?.type
        )

        if(valid) {
            response.questions = response.questions.map(question => {
                if(question.meta.type === 'rating' && !question.meta.variant) {
                    question.meta.variant = 'star'
                }

                if('min' in question) {
                    if(question.meta.type === 'rating') {
                        question.min = 1
                    } else {
                        // Scale, either 0 or 1
                        question.min = Math.min(1, question.min)
                    }
                }

                if('max' in question) {
                    if(question.meta.type === 'rating') {
                        // From 3 to 10
                        question.max = Math.min(10, Math.max(3, question.max))
                    } else {
                        // Scale, from 2 to 10
                        question.max = Math.min(10, Math.max(2, question.max))
                    }
                }

                return question
            })
        }

        return {
            ok: valid,
            response: valid ?
                response :
                null
        }
    }

    const add = async ({ strategy, prompt, questions, ...body }) => {
        setAdding(true)
        setError(false)

        const titles = questions
            ?.split('\n')
            ?.map(title => title.trim())
            ?.filter(Boolean) ?? null

        const defaults = {
            questions: !!titles?.length ?
                titles.map(title => ({
                    type: 'text',
                    multiline: false,
                    title,
                    meta: {
                        type: 'text'
                    }
                })) :
                []
        }

        let aiOk = false

        if(strategy === 'ai' && !!prompt?.length) {
            const aiResult = await getAiSuggestion(prompt)
            aiOk = aiResult.ok

            body = {
                ...body,
                ...aiResult.response
            }
        }

        let result = await addSurvey({
            ...defaults,
            ...body,
            type
        })

        if(!result.ok) {
            console.error("Error adding survey:", result.response)

            // If the AI response looked ok, but didn’t fair well with the API, then try again without it
            if(strategy === 'ai' && aiOk) {
                result = await addSurvey({
                    ...body,
                    ...defaults,
                    type
                })
            } else {
                setError(true)
                setAdding(false)
                return
            }
        }

        if(result.ok) {
           return void navigate(getSurveyUrl(result.response))
        }

        setAdding(false)
    }

    return (
        <>
            <Modal.Header
                heading={formatMessage({
                    id: 'surveys_action_create',
                    defaultMessage: 'New survey'
                })}
                caption={(type === 'custom') ?
                    formatMessage({
                        id: 'survey_type_custom_description',
                        defaultMessage: 'Define your own questions'
                    }) :
                    'Employee Net Promoter Score®'
                } />
            <Form
                json={{ disabled: true }}
                layout="vertical"
                onSubmit={add}>
                {({ errors, trigger }) => (
                    <>
                        <Modal.Body>
                            <SymbolField
                                name="symbol"
                                label={formatMessage({
                                    id: 'noun_icon',
                                    defaultMessage: 'Icon'
                                })}
                                field={{
                                    value: symbol ?? getFallbackSymbol({ type }),
                                    include: 'always',
                                    unsettable: false
                                }}
                                onChange={({ symbol }) => setSymbol(symbol)}
                                whistle={!!symbol ? 'self-contained' : type}
                                salt={salt} />
                            <StringField
                                name="title"
                                label={formatMessage({
                                    id: 'noun_title',
                                    defaultMessage: 'Title'
                                })}
                                field={{
                                    required: true,
                                    include: 'always'
                                }}
                                controlProps={{
                                    autoFocus: true,
                                    max: 255
                                }}
                                salt={salt} />
                            {(type === 'custom') && (
                                <OneOfField
                                    label={false}
                                    name="strategy"
                                    field={{
                                        value: strategy,
                                        include: 'always',
                                        options: [
                                            {
                                                value: 'ai',
                                                label: (
                                                    <LabelWithIcon>
                                                        <AiIcon
                                                            size={24}
                                                            {...(strategy === 'ai') ? { className: 'selected' } : null} />
                                                        <span>
                                                            <FormattedMessage
                                                                id="survey_ai_prompt_label"
                                                                defaultMessage="Ask AI to get you started" />
                                                        </span>
                                                    </LabelWithIcon>
                                                ),
                                                content: (
                                                    <>
                                                        <TextField
                                                            name="prompt"
                                                            label={false}
                                                            field={{
                                                                value: aiPrompt,
                                                                include: 'touched'
                                                            }}
                                                            onChange={({ prompt }) => setAiPrompt(prompt)}
                                                            salt={salt} />
                                                        <Helper>
                                                            <FormattedMessage
                                                                id="surveys_add_questions_prefill_helper_ai"
                                                                defaultMessage="Describe your desired survey in detail, or simply list your questions separated by line breaks – the AI will take care of the rest." />
                                                        </Helper>
                                                        <Beta />
                                                    </>
                                                )
                                            },
                                            {
                                                value: 'manual',
                                                label: formatMessage({
                                                    id: 'survey_questions_label',
                                                    defaultMessage: 'Prefill with questions'
                                                }),
                                                content: (
                                                    <>
                                                        <TextField
                                                            name="questions"
                                                            label={false}
                                                            field={{
                                                                value: questions,
                                                                include: 'touched'
                                                            }}
                                                            onChange={({ questions }) => setQuestions(questions)}
                                                            salt={salt} />
                                                        <Helper>
                                                            <FormattedMessage
                                                                id="surveys_add_questions_prefill_helper"
                                                                defaultMessage="Prefill with questions, separated by line breaks." />
                                                        </Helper>
                                                    </>
                                                )
                                            }
                                        ]
                                    }}
                                    onChange={({ strategy }) => setStrategy(strategy)}
                                    salt={salt} />
                            )}
                            <CheckboxField
                                label={formatMessage({
                                    id: 'survey_label_anonymous_responses',
                                    defaultMessage: 'Responses are anonymous'
                                })}
                                name="anonymous"
                                field={{
                                    value: true,
                                    include: 'always'
                                }}
                                interaction="switch"
                                enabled={type === 'custom'}
                                salt={salt} />
                            {error && (
                                <Message
                                    type="error"
                                    message={formatMessage({
                                        id: 'error_generic_try_again',
                                        defaultMessage: 'Something went wrong. Please try again.'
                                    })} />
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Actions>
                                <Plain
                                    onClick={dismiss}
                                    className="neutral"
                                    disabled={adding}>
                                    <FormattedMessage
                                        id="action_cancel"
                                        defaultMessage="Cancel" />
                                </Plain>
                                <ButtonSubmit
                                    className={cls([
                                        'constructive',
                                        adding && 'loading'
                                    ])}
                                    disabled={!!size(errors) || adding}
                                    ref={trigger}>
                                    <FormattedMessage
                                        id="action_continue"
                                        defaultMessage="Continue" />
                                </ButtonSubmit>
                            </Actions>
                        </Modal.Footer>
                    </>
                )}
            </Form>
        </>
    )
}

const systemPrompt = `
    Generate questions for a custom company survey.

    If there’s not enough context, do not invent new questions, nor new options for single or multiple select questions.

    Return as a JSON array of question objects.

    # Guidelines

    If you’re provided with questions, use those as the basis when adding the properties to the question objects. You’re not allowed to change the questions (the \`title\` key).
    If you’re not provided with questions, generate questions based on the context given.

    A question’s \`type\` can be one of the following:
    - text (short answer, \`multiline\`: \`false\` and \`meta.type: "text"\` are mandatory).
    - text (long answer, \`multiline\`: \`true\` and \`meta.type: "textarea"\` are mandatory).
    - single_select (radio buttons, \`meta.type: "radiobuttons"\` and \`options\` (directly on root level) are mandatory). \`options\` should be an array of objects, with \`value\` and \`label\` keys.
    - multiple_select (checkboxes, \`meta.type: "checkboxes"\` and \`options\` (directly on root level) are mandatory). \`options\` should be an array of objects, with \`value\` and \`label\` keys.
    - scale (linear scale type, \`meta.type: "scale"\` is mandatory, with support for \`max\` (directly on root level). Never provide a \`min\` value lower than 0, or a \`max\` value higher than 10. This scale type does not support \`meta.variant\`.
    - scale (rating type, \`meta.type: "rating"\` is mandatory, with support for \`max\` (directly on root level)) and \`meta.variant\`: \`star\` || \`emoji\` || \`heart\` || \`die\`). If the variant is \`die\`, always use \`6\` as the max. If the variant is \`emoji\`, always use \`4\` as the max. Never provide a \`min\` value lower than 0, or a \`max\` value higher than 10. Always provide a fallback value for \`meta.variant\`.
    - boolean (\`meta.type: "boolean"\` is mandatory).

    If you interpret the question as one with multiple answers (the type being \`single_select\` or \`multiple_select\`), and it looks like there are already alternatives given as part of the question, use those instead of generating new ones, and remove the alternatives from \`title\`.

    If the type is \`scale\`, always pass in \`min: 1\` on root. Never provide a \`min\` value lower than 0, or a \`max\` value higher than 10.

    Make a valid prediction what the type should be based on the question, with potentially additional props.

    # Example

    \`\`\`json
    [
        {
            "title": "What is your name?",
            "type": "text",
            "multiline": false,
            "meta": {
                "type": "text"
            }
        },
        {
            "title": "Why do you want to work here?",
            "type": "text",
            "multiline": true,
            "meta": {
                "type": "textarea"
            }
        },
        {
            "title": "What is your favorite color?",
            "type": "single_select",
            "meta": {
                "type": "radiobuttons"
            }
            "options": [
                {
                    "value": "red",
                    "label": "Red"
                },
                {
                    "value": "blue",
                    "label": "Blue"
                },
                {
                    "value": "green",
                    "label": "Green"
                }
            ]
        },
        {
            "title": "What is your favorite dish? (multiple)",
            "type": "multiple_select",
            "meta": {
                "type": "checkboxes"
            },
            "options": [
                {
                    "value": "pizza",
                    "label": "Pizza"
                },
                {
                    "value": "pasta",
                    "label": "Pasta"
                },
                {
                    "value": "burger",
                    "label": "Burger"
                }
            ]
        },
        {
            "title": "On a scale from 1 to 7, how much do you like pizza?",
            "type": "scale",
            "min": 1,
            "max": 7,
            "meta": {
                "type": "scale"
            }
        },
        {
            "title": "How much do you like meat?",
            "type": "scale",
            "min": 1,
            "max": 6,
            "meta": {
                "type": "rating",
                "variant": "die"
            }
        },
        {
            "title": "Do you like meat?",
            "type": "boolean"
        }
    ]
    \`\`\`

    # Notes

    Make sure that the following question properties are always sent, no matter the type:

    - \`title\`
    - \`type\`
    - \`meta.type\`

    The following properties should always be added to the root of the question object, and never in the nested meta object:

    - \`options\`
    - \`min\`
    - \`max\`
`

export default AddSurvey